import { ChevronDown, ChevronLeft, ChevronUp, X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';

/** A neighbour in the list the sheet was opened from: the address of its card and its name. */
export type SheetNeighbour = { readonly to: string; readonly label: string };

type EntitySheetProps = {
  /** The id of the heading that names the sheet (the entity's title, or the state shown). */
  readonly titleId: string;
  /** The list the sheet belongs to, with its filters: where «Indietro», × and Esc go. */
  readonly back: string;
  /** Router state left in the history entry the sheet closes to (e.g. whose row takes focus). */
  readonly closing?: unknown;
  readonly backLabel: string;
  readonly closeLabel: string;
  /** Previous and next in the list as it is filtered and sorted now; absent at either end. */
  readonly previous?: SheetNeighbour | null;
  readonly next?: SheetNeighbour | null;
  readonly children: ReactNode;
};

/**
 * The card's frame (decision 101, #401; the board at docs/mockups/list-and-card): every entity's
 * card sits in it, with its own address. From 900 px it stands beside the list, with previous,
 * next and ×; below, it is the page and leads back with «Indietro». Esc closes it, as it closes
 * the area's panels, unless the key belongs to a field or to an overlay above the page.
 *
 * The layout around it is the list's (`.people-layout-with-sheet` in shell.css): the card is
 * `.entity-sheet`, sticky beside the list.
 */
export function EntitySheet({
  titleId,
  back,
  closing,
  backLabel,
  closeLabel,
  previous = null,
  next = null,
  children,
}: EntitySheetProps) {
  const navigate = useNavigate();

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target !== document.body &&
        (target.closest('.shell-content') === null ||
          target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement)
      ) {
        return;
      }
      void navigate(back, { state: closing });
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, back, closing]);

  return (
    <aside aria-labelledby={titleId} className="entity-sheet">
      <div className="entity-sheet-bar">
        <span className="entity-sheet-back">
          <Button asChild variant="ghost" size="sm">
            <Link to={back} state={closing}>
              <ChevronLeft />
              {backLabel}
            </Link>
          </Button>
        </span>
        <span className="entity-sheet-steps">
          <Neighbour to={previous} icon={<ChevronUp />} />
          <Neighbour to={next} icon={<ChevronDown />} />
        </span>
        <span className="entity-sheet-close">
          <Button asChild variant="ghost" size="icon">
            <Link to={back} state={closing} aria-label={closeLabel}>
              <X />
            </Link>
          </Button>
        </span>
      </div>
      {children}
    </aside>
  );
}

function Neighbour({ to, icon }: { readonly to: SheetNeighbour | null; readonly icon: ReactNode }) {
  if (to === null) {
    // Kept in place, disabled, so the other arrow does not move at the ends of the list.
    return (
      <Button variant="ghost" size="icon" disabled aria-hidden="true" tabIndex={-1}>
        {icon}
      </Button>
    );
  }
  return (
    <Button asChild variant="ghost" size="icon">
      <Link to={to.to} aria-label={to.label} replace>
        {icon}
      </Link>
    </Button>
  );
}
